import type { CurrentUser, SessionAdapter } from '@zhiyue/core';
import { BizAppConfig } from 'config/application.manifest';
import {
  createTenant as createTenantApi,
  deleteTenant as deleteTenantApi,
  getCurrentMemberUser,
  getTenantByCode as getTenantByCodeApi,
  getTenantList as getTenantListApi,
  joinTenant as joinTenantApi,
  logout as logoutApi,
  switchTenant as switchTenantApi,
} from '@/services/bff';
import type { TenantContext } from '@/services/bff/types/auth';
import { clearOpenPlatformToken } from '@/services/openPlatformToken';
import { clearMemberDirectory } from '@/state/memberDirectory';
import { readCurrentTenantUuid, saveCurrentTenantUuid } from './tenantStorage';

const clearOpenPlatformState = () => {
  clearOpenPlatformToken();
  clearMemberDirectory();
};

// 缓存租户上下文的请求 Promise，防止初始化时 fetchCurrentUser 和 fetchTenantContexts 同时触发两个请求
let bootstrapTenantListPromise: Promise<TenantContext[]> | null = null;
let bootstrapTenantListSnapshot: TenantContext[] | null = null;

const normalizeTenantList = (tenantList?: TenantContext[] | null) => {
  const normalized = tenantList || [];
  if (!readCurrentTenantUuid()) {
    saveCurrentTenantUuid(normalized[0]?.id);
  }
  return normalized;
};

const fetchTenantListFromApi = async () => {
  const result = await getTenantListApi();
  return normalizeTenantList(result);
};

export const getBootstrapTenantList = async () => {
  if (bootstrapTenantListSnapshot) {
    return bootstrapTenantListSnapshot;
  }

  if (!bootstrapTenantListPromise) {
    bootstrapTenantListPromise = fetchTenantListFromApi()
      .then((tenantList) => {
        bootstrapTenantListSnapshot = tenantList;
        return tenantList;
      })
      .finally(() => {
        bootstrapTenantListPromise = null;
      });
  }

  return bootstrapTenantListPromise;
};

const consumeBootstrapTenantList = () => {
  const cached = bootstrapTenantListSnapshot;
  bootstrapTenantListSnapshot = null;
  return cached;
};

export const sessionAdapter: SessionAdapter = {
  fetchCurrentUser: async () => {
    const user = (await getCurrentMemberUser()) as CurrentUser &
      API.CurrentUser;
    console.log('[memberUuid][frontend] current session user', {
      memberUuid: user.memberUuid,
      userUuid: user.uuid,
      tenantUuid: user.tenantUuid,
      organizationUuid: user.organizationUuid,
    });
    saveCurrentTenantUuid(user.tenantUuid);

    return user;
  },
  fetchTenants: async () => {
    const cachedTenantList = consumeBootstrapTenantList();
    if (cachedTenantList) {
      return cachedTenantList;
    }

    const result = await getBootstrapTenantList();
    consumeBootstrapTenantList();
    return result;
  },
  switchTenant: async (organizationUuid: string) => {
    clearOpenPlatformState();
    const result = await switchTenantApi(organizationUuid);
    saveCurrentTenantUuid(result?.tenantUuid);
  },
  createTenant: async ({ tenantName }: { tenantName: string }) => {
    clearOpenPlatformState();
    const result = await createTenantApi({ tenantName });
    saveCurrentTenantUuid(result?.tenantUuid);
    return result;
  },
  lookupTenantByCode: async (code: string) => getTenantByCodeApi(code),
  joinTenant: async (payload: { tenantUuid: string; nodeId: string }) => {
    clearOpenPlatformState();
    const result = await joinTenantApi(payload);
    saveCurrentTenantUuid(result?.tenantUuid);
    return result;
  },
  deleteTenant: async (tenantUuid: string) => {
    clearOpenPlatformState();
    const result = await deleteTenantApi(tenantUuid);
    saveCurrentTenantUuid(result?.tenantUuid || null);
    return result;
  },
  logout: async () => {
    const logoutBaseUrl = BizAppConfig.appSsoLogoutUrl;
    const logoutUrl = `${logoutBaseUrl}?redirect_uri=${encodeURIComponent(`${window.location.origin}/user/login`)}`;

    try {
      await logoutApi(BizAppConfig.appSsoClientId as string);
    } catch {
      // Keep local logout flow even when backend logout fails.
    } finally {
      // 登出时清除租户上下文缓存
    }

    clearOpenPlatformState();
    saveCurrentTenantUuid(null);
    return { logoutUrl };
  },
};
